/**
 * Templates page — operator-facing view of the available Template
 * packages. The main card shows a friendly description and primary
 * actions; technical details (Template ID / SHA-256 / etc.) live in
 * a collapsible "技术详情" panel.
 */
import {useEffect, useState} from 'react';
import {ApiError} from '../lib/api';
import {templatesApi, type Template} from '../lib/api-resources';
import {friendlyError} from '../lib/error-labels';

function capabilityBadges(caps: Record<string, boolean> | null) {
  if (!caps) return null;
  const list = Object.entries(caps).filter(([, v]) => v === true);
  if (list.length === 0) return <span className="muted">（无）</span>;
  return (
    <div className="capability-list">
      {list.map(([k]) => (
        <span key={k} className="capability-chip">{k}</span>
      ))}
    </div>
  );
}

function templateCard(pkg: Template) {
  return (
    <div key={pkg.id} className="template-card template-card-product">
      <div className="template-card-header">
        <div className="template-card-title">
          <h3>{pkg.name}</h3>
          <code className="muted">{pkg.template_id}@{pkg.version}</code>
        </div>
        <div className="template-card-meta">
          {pkg.is_latest && <span className="tag tag-success">最新</span>}
        </div>
      </div>
      {pkg.description && <p className="template-card-desc">{pkg.description}</p>}

      <div className="template-card-product-actions">
        <button className="btn-secondary" type="button" disabled>
          预览模板
        </button>
        <button className="btn-primary" type="button" disabled>
          应用到网站
        </button>
      </div>

      <details className="template-card-tech">
        <summary>查看技术详情</summary>
        <div className="template-tech-grid">
          <div>
            <span className="muted">Template ID：</span> <code>{pkg.template_id}</code>
          </div>
          <div>
            <span className="muted">主题：</span> <code>{pkg.theme_slug}</code>
          </div>
          <div>
            <span className="muted">包版本：</span> <code>{pkg.version}</code>
          </div>
          <div>
            <span className="muted">契约版本：</span> <code>{pkg.contract_version}</code>
          </div>
          <div>
            <span className="muted">SHA-256：</span>{' '}
            <code title={pkg.sha256}>{pkg.sha256.slice(0, 16)}…</code>
          </div>
          <div>
            <span className="muted">源路径：</span> <code>{pkg.source_path}</code>
          </div>
        </div>
        <div className="template-tech-caps">
          <span className="muted">能力：</span>
          {capabilityBadges(pkg.capabilities)}
        </div>
      </details>
    </div>
  );
}

export default function TemplatesPage() {
  const {t: _t} = {t: (k: string) => k};  // i18n kept for future; strings are static here
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    setLoading(true);
    try {
      const r = await templatesApi.list();
      setTemplates(r.templates);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;
      setError(friendlyError(code, e instanceof Error ? e.message : String(e)).headline);
    } finally {
      setLoading(false);
    }
  }

  async function refresh() {
    setRefreshing(true);
    setError(null);
    try {
      const r = await templatesApi.refresh();
      setTemplates(r.templates);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;
      setError(friendlyError(code, e instanceof Error ? e.message : String(e)).headline);
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>模板</h1>
          <p className="muted">
            选择一个模板应用到你的站点，模板包含页面结构、区块设计和默认内容。
          </p>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={refresh} disabled={refreshing}>
            {refreshing ? '刷新中…' : '刷新模板'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="muted">正在加载模板…</div>
      ) : templates.length === 0 ? (
        <div className="empty-state">
          <p>还没有可用的模板。</p>
          <p className="muted">
            如果你是管理员：把模板目录放到 <code>wp-theme/</code> 根目录（必须包含 <code>contract.json</code> 和合法的主题），然后点「刷新模板」。
          </p>
        </div>
      ) : (
        <div className="template-grid">
          {templates.map(templateCard)}
        </div>
      )}
    </div>
  );
}
